import { RefObject, startTransition, useEffect, useRef, useState } from 'react';

export const MATRIX_CELL_SIZE = 35;
export const MATRIX_ROW_OFFSET = 110;

const OVERSCAN_ROWS = 6;
const OVERSCAN_COLS = 6;

export type MatrixWindow = {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
};

const buildWindow = (
  scrollTop: number,
  scrollLeft: number,
  clientHeight: number,
  clientWidth: number,
  leftWidth: number,
  rowCount: number,
  colCount: number,
): MatrixWindow => {
  const startRow = Math.max(
    0,
    Math.floor((scrollTop - MATRIX_ROW_OFFSET) / MATRIX_CELL_SIZE) -
      OVERSCAN_ROWS,
  );
  const endRow = Math.min(
    rowCount,
    Math.ceil(
      (scrollTop + clientHeight - MATRIX_ROW_OFFSET) / MATRIX_CELL_SIZE,
    ) + OVERSCAN_ROWS,
  );
  const startCol = Math.max(
    0,
    Math.floor((scrollLeft - leftWidth) / MATRIX_CELL_SIZE) - OVERSCAN_COLS,
  );
  const endCol = Math.min(
    colCount,
    Math.ceil((scrollLeft + clientWidth - leftWidth) / MATRIX_CELL_SIZE) +
      OVERSCAN_COLS,
  );

  return { startRow, endRow, startCol, endCol };
};

const initialWindow = (rowCount: number, colCount: number): MatrixWindow => ({
  startRow: 0,
  endRow: Math.min(rowCount, 36),
  startCol: 0,
  endCol: Math.min(colCount, 40),
});

const isSameWindow = (a: MatrixWindow, b: MatrixWindow) =>
  a.startRow === b.startRow &&
  a.endRow === b.endRow &&
  a.startCol === b.startCol &&
  a.endCol === b.endCol;

type UseMatrixWindowArgs = {
  scrollRef: RefObject<HTMLDivElement>;
  leftRef: RefObject<HTMLDivElement>;
  rowCount: number;
  colCount: number;
  enabled: boolean;
};

export const useMatrixWindow = ({
  scrollRef,
  leftRef,
  rowCount,
  colCount,
  enabled,
}: UseMatrixWindowArgs): MatrixWindow => {
  const [matrixWindow, setMatrixWindow] = useState<MatrixWindow>(() =>
    initialWindow(rowCount, colCount),
  );
  const leftWidthRef = useRef(200);
  const latestRef = useRef(matrixWindow);
  latestRef.current = matrixWindow;

  useEffect(() => {
    if (!enabled) {
      const next = initialWindow(rowCount, colCount);
      setMatrixWindow(next);
      latestRef.current = next;
      return undefined;
    }

    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      const next = initialWindow(rowCount, colCount);
      setMatrixWindow(next);
      latestRef.current = next;
      return undefined;
    }

    let rafId = 0;

    const update = () => {
      if (leftRef.current) {
        leftWidthRef.current = leftRef.current.offsetWidth;
      }
      const next = buildWindow(
        scrollEl.scrollTop,
        scrollEl.scrollLeft,
        scrollEl.clientHeight,
        scrollEl.clientWidth,
        leftWidthRef.current,
        rowCount,
        colCount,
      );
      if (isSameWindow(latestRef.current, next)) {
        return;
      }
      latestRef.current = next;
      startTransition(() => {
        setMatrixWindow(next);
      });
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };

    update();
    scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleUpdate)
        : null;
    if (resizeObserver) {
      resizeObserver.observe(scrollEl);
      if (leftRef.current) {
        resizeObserver.observe(leftRef.current);
      }
    }

    return () => {
      scrollEl.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [scrollRef, leftRef, rowCount, colCount, enabled]);

  return matrixWindow;
};
